/**
 * @bos/portal3 — the projects query/mutation layer (M4).
 *
 * Two small hooks wrapping the typed tRPC client — the projects analogue of AuthContext's
 * method surface, but page-scoped rather than app-global (the list belongs to the
 * dashboard, a single project to its page, so nothing global needs to hold them):
 *
 *   - useProjectList()  → the caller's projects with loading/error/reload + create. The
 *     server already returns them owner-scoped and ordered (active first); the client just
 *     renders what it is given.
 *   - useProject(id)    → one project by id, with a distinct `not-found` status (so a
 *     deep-linked or someone-else's id renders a clean not-found page, never a crash) plus
 *     rename / archive / unarchive.
 *
 * No react-query: a little useState + one effect is all this needs, matching AuthContext.
 */
import { useCallback, useEffect, useState } from 'react';
import { TRPCClientError } from '@trpc/client';
import type { Project, ProjectListRow } from '@bos/portal3-server';
import { client } from '../rpc/client';

export type { Project, ProjectListRow };

/** Turn a thrown tRPC error into a message safe to render (zod issues → first message). */
export function projectErrorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) {
    const raw: string = err.message ?? '';
    try {
      const issues = JSON.parse(raw) as Array<{ message?: string }>;
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      /* not a zod issue array — fall through to the plain message */
    }
    return raw || 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Please try again.';
}

/** True when a thrown tRPC error is a NOT_FOUND (missing OR not the caller's — same signal). */
function isNotFound(err: unknown): boolean {
  return err instanceof TRPCClientError && err.data?.code === 'NOT_FOUND';
}

// ── the list (dashboard) ─────────────────────────────────────────────────────────

export type ListStatus = 'loading' | 'ready' | 'error';

/** What the create form supplies (owner is server-side; jurisdiction stays server-only). */
export interface CreateProjectInput {
  name: string;
  description?: string;
  industry?: string;
}

export interface UseProjectList {
  /** Each row carries `waitingRunId`: the build of that business the machine has stopped
   *  on, waiting for its owner — the dashboard's one honest run signal. */
  readonly projects: ProjectListRow[];
  readonly status: ListStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
  /** Create a project and prepend it (it is the newest active → belongs at the front). */
  create: (input: CreateProjectInput) => Promise<Project>;
}

export function useProjectList(): UseProjectList {
  const [projects, setProjects] = useState<ProjectListRow[]>([]);
  const [status, setStatus] = useState<ListStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setProjects(await client.projects.list.query());
      setStatus('ready');
    } catch (err) {
      setError(projectErrorMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(async (input: CreateProjectInput) => {
    const created = await client.projects.create.mutate(input);
    // A business created a second ago has no builds at all, so nothing is waiting on
    // anyone and there is no build to link — both nulls are facts, not placeholders.
    setProjects((prev) => [{ ...created, waitingRunId: null, latestRun: null }, ...prev]);
    return created;
  }, []);

  return { projects, status, error, reload, create };
}

// ── a single project (project page) ──────────────────────────────────────────────

export type ProjectStatus = 'loading' | 'ready' | 'not-found' | 'error';

/** The editable business details of a project (M6). */
export interface UpdateProjectInput {
  name: string;
  description?: string;
  industry?: string;
}

export interface UseProject {
  readonly project: Project | null;
  readonly status: ProjectStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
  /** Save the project's business details (name + description + industry) in one call. */
  update: (fields: UpdateProjectInput) => Promise<void>;
  archive: () => Promise<void>;
  unarchive: () => Promise<void>;
}

export function useProject(id: string): UseProject {
  const [project, setProject] = useState<Project | null>(null);
  const [status, setStatus] = useState<ProjectStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const p = await client.projects.get.query({ id });
      setProject(p);
      setStatus('ready');
    } catch (err) {
      if (isNotFound(err)) {
        setProject(null);
        setStatus('not-found');
      } else {
        setError(projectErrorMessage(err));
        setStatus('error');
      }
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const update = useCallback(
    async (fields: UpdateProjectInput) => {
      const updated = await client.projects.update.mutate({ id, ...fields });
      setProject(updated);
      setStatus('ready');
    },
    [id],
  );

  const archive = useCallback(async () => {
    const updated = await client.projects.archive.mutate({ id });
    setProject(updated);
    setStatus('ready');
  }, [id]);

  const unarchive = useCallback(async () => {
    const updated = await client.projects.unarchive.mutate({ id });
    setProject(updated);
    setStatus('ready');
  }, [id]);

  return { project, status, error, reload, update, archive, unarchive };
}
